/**
 * UI 효과음 — Web Audio API 합성음(오디오 파일·패키지 없음). 기기별 localStorage on/off, 기본 OFF.
 * 교사 앱(MainLayout)에서만 installUiSound()로 전역 연결.
 */
/** adjust: 수치 조정 버튼(tick+pop), click: 일반 버튼(약한 단일 틱) */
export type UiSoundKind = 'click' | 'adjust' | 'pop' | 'chime';

const STORAGE_KEY = 'cp_ui_sound_enabled';
export const UI_SOUND_CHANGED_EVENT = 'cp-ui-sound-changed';
const MASTER_VOLUME = 0.6;

let ctx: AudioContext | null = null;
let lastPlayed: Record<UiSoundKind, number> = { click: 0, adjust: 0, pop: 0, chime: 0 };

export function readUiSoundEnabled(): boolean {
  try {
    /* 기본 ON: 값이 없으면 켜짐, 명시적 '0'만 꺼짐 */
    return window.localStorage.getItem(STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function writeUiSoundEnabled(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(UI_SOUND_CHANGED_EVENT, { detail: { enabled: on } }));
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx || ctx.state === 'closed') {
    const C =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!C) return null;
    try {
      ctx = new C();
    } catch {
      return null;
    }
  }
  return ctx;
}

/** 사용자 제스처 안에서 동기적으로 호출 — 컨텍스트 생성 + resume + 무음 버퍼 재생(iOS/Safari 잠금 해제) */
export function unlockUiSound(): void {
  const ac = getCtx();
  if (!ac) return;
  try {
    if (ac.state !== 'running') void ac.resume().catch(() => undefined);
    const buf = ac.createBuffer(1, 1, 22050);
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(ac.destination);
    src.start(0);
  } catch {
    /* ignore */
  }
}

/* 쇼츠 홍보영상(ep1 audio.py) SFX를 그대로 이식: tick(짧은 사인 + 빠른 지수감쇠), pop(지수 상승 스윕 + sin² 엔벨로프), chime(기음+옥타브, 감쇠 τ0.25s) */
function voice(ac: AudioContext, f: number, start: number, dur: number) {
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(f, ac.currentTime + 0.005 + start);
  osc.connect(g);
  g.connect(ac.destination);
  const t = ac.currentTime + 0.005 + start;
  return { osc, g, t, end: t + dur };
}
function tick(ac: AudioContext, start: number, f = 2600, d = 0.03, amp = 0.5) {
  const v = voice(ac, f, start, d);
  v.g.gain.setValueAtTime(amp * MASTER_VOLUME, v.t);
  v.g.gain.setTargetAtTime(0, v.t, 0.008);
  v.osc.start(v.t);
  v.osc.stop(v.end + 0.01);
}
const SIN2: Float32Array = (() => {
  const c = new Float32Array(32);
  for (let i = 0; i < 32; i += 1) c[i] = Math.sin((Math.PI * i) / 31) ** 2;
  return c;
})();
function pop(ac: AudioContext, start: number, f0 = 600, f1 = 1400, d = 0.08, amp = 0.25) {
  const v = voice(ac, f0, start, d);
  v.osc.frequency.exponentialRampToValueAtTime(f1, v.end);
  const curve = new Float32Array(SIN2.length);
  for (let i = 0; i < curve.length; i += 1) curve[i] = SIN2[i] * amp * MASTER_VOLUME;
  v.g.gain.setValueCurveAtTime(curve, v.t, d);
  v.osc.start(v.t);
  v.osc.stop(v.end + 0.01);
}
/* 팝업 등장(차분형): 단일 사인 450→900Hz, 110ms — 10ms 부드러운 어택 후 빠른 감쇠, 피크 0.2 */
function modalPop(ac: AudioContext) {
  const v = voice(ac, 450, 0, 0.11);
  v.osc.frequency.exponentialRampToValueAtTime(900, v.end);
  v.g.gain.setValueAtTime(0, v.t);
  v.g.gain.linearRampToValueAtTime(0.2 * MASTER_VOLUME, v.t + 0.01);
  v.g.gain.setTargetAtTime(0, v.t + 0.01, 0.03);
  v.osc.start(v.t);
  v.osc.stop(v.end + 0.06);
}
function chime(ac: AudioContext, start: number, f = 1046, d = 0.8, amp = 0.2) {
  [
    [f, 1],
    [2 * f, 0.3],
  ].forEach(([freq, rel]) => {
    const v = voice(ac, freq, start, d);
    v.g.gain.setValueAtTime(0, v.t);
    v.g.gain.linearRampToValueAtTime(amp * rel * MASTER_VOLUME, v.t + 0.006);
    v.g.gain.setTargetAtTime(0, v.t + 0.006, 0.25);
    v.osc.start(v.t);
    v.osc.stop(v.end + 0.01);
  });
}

const MIN_GAP: Record<UiSoundKind, number> = { click: 60, adjust: 60, pop: 200, chime: 250 };

export function playUiSound(kind: UiSoundKind): void {
  if (!readUiSoundEnabled()) return;
  const now = Date.now();
  if (now - lastPlayed[kind] < MIN_GAP[kind]) return;
  lastPlayed = { ...lastPlayed, [kind]: now };
  const ac = getCtx();
  if (!ac) return;
  if (ac.state !== 'running') void ac.resume().catch(() => undefined);
  try {
    if (kind === 'click') {
      /* 일반 버튼: 낮은 중역 단일 소프트 틱(1000Hz, ~30ms, 스윕 없음, 수치 버튼의 ~45% 음량) */
      tick(ac, 0, 1000, 0.03, 0.22);
    } else if (kind === 'adjust') {
      /* 수치 조정 버튼 — 영상의 버튼 탭: tick(2600Hz) + 20ms 뒤 pop(600→1400Hz, 80ms) */
      tick(ac, 0);
      pop(ac, 0.02);
    } else if (kind === 'pop') {
      /* 팝업 등장(차분형 단일음): 사인 450→900Hz, 110ms, 피크 0.2 */
      modalPop(ac);
    } else {
      /* 영상의 저장/완료: tick×2(1800Hz) → pop(500→1500Hz) → chime(C6 1046Hz) */
      tick(ac, 0, 1800, 0.04);
      tick(ac, 0.15, 1800, 0.04);
      pop(ac, 0.4, 500, 1500, 0.12, 0.4);
      chime(ac, 0.5, 1046, 0.8, 0.2);
    }
  } catch {
    /* ignore */
  }
}

/** 수치 조정(스테퍼 ±) 버튼 — 기존 클래스·aria-label 기준, data-sfx="adjust"로 추가 지정 가능 */
const ADJUST_SEL = [
  '[data-sfx="adjust"]',
  '.cp-pin-stu-ctrl-btn',
  '.cp-pin-bulk-step-btn',
  '.cp-apn-create-inc-btn',
  '.cp-slide-dice-step-btn',
  '.cp-btn-plusminus',
  '[class*="-step-btn"]',
  '[aria-label$="증가"]',
  '[aria-label$="감소"]',
  '[aria-label$="늘리기"]',
  '[aria-label$="줄이기"]',
].join(', ');
export function classifyUiSoundTarget(btn: Element): UiSoundKind | null {
  if (btn.closest('[data-sfx="none"]')) return null;
  try {
    if (btn.matches(ADJUST_SEL)) return 'adjust';
  } catch {
    /* ignore */
  }
  return 'click';
}

const BUTTON_SEL =
  'button, a[href], [role="button"], [role="link"], [role="menuitem"], [role="tab"], [role="option"], [role="checkbox"], [role="switch"], [data-sfx], label:has(> input[type="checkbox"])';
const MODAL_SEL =
  '[role="dialog"], [aria-modal="true"], .modal-overlay, .modal, [class*="-modal-overlay"], [class*="-modal-backdrop"], [class*="modal-backdrop"]';

/** 전역 연결 — 반환값으로 해제. 끈 상태에선 아무 소리 없음 */
export function installUiSound(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => undefined;
  let clickedAt = 0;
  const findBtn = (t: Element | null): HTMLElement | null => {
    try {
      return (t?.closest?.(BUTTON_SEL) as HTMLElement | null) ?? null;
    } catch {
      /* :has 미지원 브라우저 */
      return (t?.closest?.(BUTTON_SEL.replace(/, label:has\([^)]*\)/, '')) as HTMLElement | null) ?? null;
    }
  };
  /* 클릭 vs 드래그 구분: 누른 위치·시각을 기록하고, 완료된 click에서만 재생
     (6px 이상 이동·dragstart·600ms 이상 길게 누름이면 생략, 키보드 click(detail 0)은 재생) */
  const MOVE_PX = 6;
  const LONG_MS = 600;
  let press: { x: number; y: number; t: number; dragged: boolean } | null = null;
  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) {
      press = null;
      return;
    }
    press = { x: e.clientX, y: e.clientY, t: Date.now(), dragged: false };
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!press || press.dragged) return;
    if (Math.abs(e.clientX - press.x) >= MOVE_PX || Math.abs(e.clientY - press.y) >= MOVE_PX) press.dragged = true;
  };
  const onDragStart = () => {
    if (press) press.dragged = true;
  };
  const onClick = (e: MouseEvent) => {
    const p = press;
    press = null;
    if (!readUiSoundEnabled()) return;
    if (e.button !== 0) return;
    const keyboard = e.detail === 0 && !p;
    if (!keyboard && p) {
      if (p.dragged) return;
      if (Date.now() - p.t > LONG_MS) return;
      if (Math.abs(e.clientX - p.x) >= MOVE_PX || Math.abs(e.clientY - p.y) >= MOVE_PX) return;
    }
    const btn = findBtn(e.target as Element | null);
    if (!btn) return;
    if ((btn as HTMLButtonElement).disabled || btn.getAttribute('aria-disabled') === 'true') return;
    const kind = classifyUiSoundTarget(btn);
    if (!kind) return;
    clickedAt = Date.now();
    playUiSound(kind);
  };
  /* 제스처마다 잠금 해제 시도(Chrome: pointerdown/keydown, Safari·iOS: touchend/click) — 켜져 있을 때만 */
  const onGesture = () => {
    if (readUiSoundEnabled()) unlockUiSound();
  };
  const GESTURES = ['pointerdown', 'keydown', 'touchend', 'click'] as const;
  GESTURES.forEach((ev) => document.addEventListener(ev, onGesture, true));
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('dragstart', onDragStart, true);
  document.addEventListener('click', onClick, true);

  const isModalEl = (el: Element) => {
    try {
      return el.matches(MODAL_SEL) || !!el.querySelector?.(':scope > [role="dialog"], :scope > [aria-modal="true"]');
    } catch {
      return false;
    }
  };
  const mo =
    typeof MutationObserver !== 'undefined'
      ? new MutationObserver((records) => {
          if (!readUiSoundEnabled()) return;
          for (const r of records) {
            for (let i = 0; i < r.addedNodes.length; i += 1) {
              const n = r.addedNodes[i];
              if (n.nodeType === 1 && isModalEl(n as Element)) {
                /* 클릭음 직후라면 살짝 늦춰 겹침 완화 */
                const wait = Math.max(0, 40 - (Date.now() - clickedAt));
                window.setTimeout(() => playUiSound('pop'), wait);
                return;
              }
            }
          }
        })
      : null;
  mo?.observe(document.body, { childList: true, subtree: true });
  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('pointermove', onPointerMove, true);
    document.removeEventListener('dragstart', onDragStart, true);
    document.removeEventListener('click', onClick, true);
    GESTURES.forEach((ev) => document.removeEventListener(ev, onGesture, true));
    mo?.disconnect();
  };
}

/** on/off 변경 구독(같은 탭 이벤트 + 다른 탭 storage) — 반환값으로 해제 */
export function subscribeUiSoundEnabled(cb: (enabled: boolean) => void): () => void {
  const onLocal = () => cb(readUiSoundEnabled());
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb(readUiSoundEnabled());
  };
  window.addEventListener(UI_SOUND_CHANGED_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(UI_SOUND_CHANGED_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
  };
}

/** 토글(제스처 안에서 호출) — 켜면 확인용 pop */
export function toggleUiSoundEnabled(): boolean {
  const next = !readUiSoundEnabled();
  writeUiSoundEnabled(next);
  if (next) {
    unlockUiSound();
    playUiSound('pop');
  }
  return next;
}
